import React from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Center,
  Grid,
  ThemeIcon,
  Box,
  Image
} from '@mantine/core';
import { IconCloud, IconWorld, IconPhone, IconTool, IconHeadset, IconPhoneCheck, IconRobot, IconBrain } from '@tabler/icons-react';

function PlatformSelector({ platforms, onSelect }) {
  // Icon mapping for platforms
  const platformIcons = {
    aws: IconCloud,
    cisco: IconWorld,
    five9: IconPhone,
    satools: IconTool,
    ccutils: IconHeadset,
    sit: IconPhoneCheck,
    agentstudio: IconRobot,
    ivr: IconBrain
  };

  // Color mapping for platforms
  const platformColors = {
    aws: 'orange',
    cisco: 'blue',
    five9: 'green',
    satools: 'purple',
    ccutils: 'teal',
    sit: 'violet',
    agentstudio: 'cyan',
    ivr: 'indigo'
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px 20px'
      }}
    >
      <Container size="lg">
        <Center>
          <Stack align="center" gap="xl" style={{ maxWidth: '800px' }}>
            {/* Header Section — logo is the wordmark, no need for redundant H1 text */}
            <Stack align="center" gap="lg">
              <Image
                src="./Presidio-blue-logo.svg"
                alt="Presidio"
                fit="contain"
                style={{
                  width: 'min(560px, 90%)',
                  maxHeight: '160px',
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <Text
                size="xl"
                style={{ color: 'rgba(255, 255, 255, 0.92)', textAlign: 'center' }}
              >
                Contact Center configuration, audit, and automation
              </Text>
            </Stack>

            {/* Platform Selection Grid */}
            <Grid gutter="xl" style={{ width: '100%', maxWidth: '900px' }}>
              {Object.entries(platforms).map(([key, platform]) => {
                const IconComponent = platformIcons[key];
                const color = platformColors[key];

                return (
                  <Grid.Col key={key} span={{ base: 12, sm: 6, md: 4 }}>
                    <Card
                      shadow="xl"
                      padding="xl"
                      radius="md"
                      onClick={() => onSelect(key)}
                      style={{
                        background: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: '350px',
                        display: 'flex',
                        flexDirection: 'column',
                        border: `2px solid transparent`,
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)';
                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.border = `2px solid var(--mantine-color-${color}-6)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.border = '2px solid transparent';
                      }}
                    >
                      <Stack align="center" gap="md" style={{ flex: 1, justifyContent: 'space-between', padding: '10px 0' }}>
                        <Stack align="center" gap="md">
                          <ThemeIcon
                            size={70}
                            radius="md"
                            color={color}
                            variant="light"
                          >
                            <IconComponent size={40} />
                          </ThemeIcon>

                          <Title order={3} size="1.3rem" fw={600} align="center" style={{ marginBottom: '5px' }}>
                            {platform.name}
                          </Title>
                        </Stack>

                        <Text
                          size="sm"
                          color="dimmed"
                          align="center"
                          style={{
                            lineHeight: 1.4,
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 10px',
                            textAlign: 'center'
                          }}
                        >
                          {platform.description}
                        </Text>

                        <Text
                          size="xs"
                          color={color}
                          align="center"
                          fw={600}
                          style={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            opacity: 0.8,
                            marginTop: '5px'
                          }}
                        >
                          Click to select
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                );
              })}
            </Grid>

            {/* Footer Info */}
            <Stack align="center" gap="xs" style={{ marginTop: '40px' }}>
              <Text
                size="sm"
                style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}
              >
                Professional contact center migration and configuration tools
              </Text>
              <Text
                size="xs"
                style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center' }}
              >
                Presidio Networked Solutions, Inc.
              </Text>
            </Stack>
          </Stack>
        </Center>
      </Container>
    </Box>
  );
}

export default PlatformSelector;